"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Search, ShieldAlert, Dumbbell, PersonStanding, ArrowRight } from "lucide-react";
const steps = [{
  number: "01",
  title: "Call & Diagnosis",
  description: "Contact our expert team for a rapid response. We'll discuss your boiler issues and schedule a priority visit from an RGI technician.",
  icon: Search
}, {
  number: "02",
  title: "On-site Repair",
  description: "Our engineers arrive fully equipped to diagnose and fix the fault on the spot, using high-quality parts to ensure lasting reliability.",
  icon: ShieldAlert
}, {
  number: "03",
  title: "Safety Check",
  description: "Every repair includes a comprehensive gas safety inspection to ensure your system is operating efficiently and safely for your home.",
  icon: Dumbbell
}, {
  number: "04",
  title: "Certified Peace",
  description: "Receive your digital service record and safety documentation, with full insurance coverage.",
  icon: PersonStanding
}];
interface StepCardProps {
  step: typeof steps[0];
  index: number;
  "data-index": number;
}
function StepCard({
  step,
  index,
  "data-index": dataIndex
}: StepCardProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.5,
    once: false
  });
  const Icon = step.icon;
  return <div ref={ref} data-index={dataIndex} className={`relative flex-1 flex flex-col p-8 rounded-2xl border-2 transition-all duration-700 overflow-hidden bg-card backdrop-blur-md ${isVisible ? "border-primary shadow-xl shadow-primary/5 -translate-y-2" : "border-border"}`}>
      {}
      <span className="absolute -bottom-6 -right-4 text-[12rem] font-default font-bold text-primary/[0.03] leading-none z-0 pointer-events-none select-none transition-opacity duration-700">
        {step.number}
      </span>

      <div className="relative z-10 flex flex-col h-full">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 transition-colors duration-700 ${isVisible ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
          <Icon className="w-6 h-6" />
        </div>
        
        <h3 className="text-2xl font-default font-bold mb-4 text-foreground tracking-tight">
          {step.title}
        </h3>
        
        <p className="text-muted-foreground font-default leading-relaxed flex-grow">
          {step.description}
        </p>
      </div>
    </div>;
}
export default function BoilerRepairDublin() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="263664" id="boiler-repair-dublin" className="bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-lg blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-primary/5 rounded-lg blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 lg:mb-24 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-sm font-mono font-semibold tracking-widest text-primary uppercase mb-4">
            Our Process
          </h2>
          <h3 className="text-4xl lg:text-5xl font-default font-bold mb-6 text-foreground tracking-tighter">
            The D4 Professional Repair Journey
          </h3>
          <p className="text-lg text-muted-foreground font-default leading-relaxed">
            We understand that a broken boiler is an emergency. Our streamlined four-step process ensures your heating is restored quickly, safely, and professionally by RGI registered experts.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="hidden lg:block absolute top-1/2 left-0 w-full h-0.5 bg-border -translate-y-1/2 z-0" />
          
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative z-10">
            {steps.map((step, index) => <StepCard key={step.number} step={step} index={index} data-index={index} />)}
          </div>
        </div>

        <div className="mt-20 text-center flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button size="lg" asChild className="w-full sm:w-auto text-base h-14 px-8 bg-primary hover:bg-primary/90 font-default font-semibold shadow-lg shadow-primary/20">
            <Link to="/ContactPage">
              Book Emergency Repair
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild className="w-full sm:w-auto text-base h-14 px-8 border-primary text-primary hover:bg-primary/5 font-default font-semibold">
            <Link to="/BoilerRepairPage">
              View Repair Services
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}